import {
    InfiniteData,
    keepPreviousData,
    useInfiniteQuery,
    useQueryClient
} from '@tanstack/react-query';

import {StringSearch} from '../../hooks/requests/use-categories-query';
import {apiClient} from '../../../../../utils/api-client';
import {CategoriesSearch, getCategoriesSearch} from '../../../../../utils/get-categories-search';

export const SEARCH_DOCS_PAGE_SIZE = 60;

interface Props {
    searchText?: string;
    versionId: number;
    categories?: string[];
    validationStatus?: ValidationStatus;
}

interface ViewModeSearchDocumentsOptions {
    stringSearch?: StringSearch;
    types?: DocType[];
    categories?: string[];
    categoriesSearch?: CategoriesSearch;
    readOnly?: boolean;
    limit: number;
    cursor: DocumentCursor | null;
    validationStatus?: ValidationStatus;
}

export function useSearchDocumentsInfiniteQuery(props: Props) {
    const searchString = props.searchText?.trim();

    return useInfiniteQuery({
        queryKey: [
            'searchDocsViewMode',
            searchString,
            props.versionId,
            props.categories,
            props.validationStatus
        ],
        queryFn: async ({pageParam}) => {
            const requestBody: ViewModeSearchDocumentsOptions = {
                stringSearch: searchString ? {strict: false, value: searchString} : undefined,
                categoriesSearch: getCategoriesSearch(props.categories),
                validationStatus: props.validationStatus,
                cursor: pageParam.cursor,
                limit: pageParam.limit
            };

            return apiClient.post<PageResponse<ViewModeDoc>>({
                path: `documents-revisions-service/api/viewer/documents?global-version=${props.versionId}`,
                parameters: requestBody
            });
        },
        getNextPageParam: ({nextCursor, remaining}) => {
            const hasNextPage = remaining > 0;
            if (hasNextPage) {
                return {
                    cursor: nextCursor,
                    limit: SEARCH_DOCS_PAGE_SIZE
                };
            }
        },
        initialPageParam: {cursor: null, limit: SEARCH_DOCS_PAGE_SIZE} as PageParams,
        placeholderData: keepPreviousData,
        gcTime: 0
    });
}

/** Docs search query cache controls */
export function useSearchDocsInfiniteQueryCache() {
    const queryClient = useQueryClient();

    function setIsDocumentFavorite(documentId: number, isFavorite: boolean) {
        queryClient.setQueriesData<InfiniteData<PageResponse<ViewModeDoc>>>(
            {
                queryKey: ['searchDocsViewMode'],
                exact: false
            },
            (data) => {
                if (!data) {
                    return data;
                }

                return {
                    ...data,
                    pages: data.pages.map((page) => {
                        return {
                            ...page,
                            data: page.data.map((document) => {
                                if (document.id === documentId) {
                                    return {
                                        ...document,
                                        userFavorite: isFavorite
                                    };
                                }

                                return document;
                            })
                        };
                    })
                };
            }
        );
    }

    return {setIsDocumentFavorite};
}
